import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { PredictionFormComponent } from '../manage-predictions/prediction-form/prediction-form.component';
import { PredictionService } from '../../../services/prediction.service';
import { Store } from '@ngrx/store';
import { selectSports } from '../../../store/selectors/sport.selectors';
import { toSignal } from '@angular/core/rxjs-interop';
import { selectTeams } from '../../../store/selectors/teams.selectors';
import { selectCompetitions } from '../../../store/selectors/competitions.selectors';
import { createPrediction } from '../../../store/actions/prediction.actions';

export interface CustomPredictionRequest {
  id: number;
  user_id: number;
  user_name?: string;
  user_email?: string;
  requested_team_home_name: string;
  requested_team_away_name: string;
  requested_sport_name: string;
  requested_competition_name: string;
  requested_event_date: string;
  price_credits: number;
  status: 'pending' | 'processing' | 'completed' | 'cancelled';
  purchase_date: string;
  prediction_id?: number;
}

@Component({
  selector: 'app-unit-prediction',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    TableModule,
    TagModule,
    DropdownModule,
    InputTextModule,
    CalendarModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    PredictionFormComponent
  ],
  templateUrl: './unit-prediction.component.html',
  styleUrls: ['./unit-prediction.component.scss'],
  providers: [MessageService, ConfirmationService]
})
export class UnitPredictionComponent implements OnInit {
  private messageService = inject(MessageService);
  private predictionService = inject(PredictionService);
  private confirmationService = inject(ConfirmationService);
  private store = inject(Store);
  customRequests = signal<CustomPredictionRequest[]>([]);
  isLoading = signal<boolean>(false);
  selectedRequest: CustomPredictionRequest | null = null;
  sports = toSignal(this.store.select(selectSports));
  teams = toSignal(this.store.select(selectTeams))
  competitions = toSignal(this.store.select(selectCompetitions))
  showPredictionFormDialog = false;
  showDetailsDialog = false;
  cancelDialogVisible = false;
  cancelNote: string = '';
  isMobile = window.innerWidth < 768;

  customErrors = {
    dateError: false,
    isEqualTeams: false
  };

  statusFilter = '';
  sportFilter = '';
  dateFilter: Date | null = null;

  statusOptions = [
    { label: 'Tous les statuts', value: '' },
    { label: 'En attente', value: 'pending' },
    { label: 'En cours', value: 'processing' },
    { label: 'Terminé', value: 'completed' },
    { label: 'Annulé', value: 'cancelled' }
  ];

  sportOptions = [
    { label: 'Tous les sports', value: '' },
    { label: 'Football', value: 'football' },
    { label: 'Basketball', value: 'basketball' },
    { label: 'Tennis', value: 'tennis' },
    { label: 'Rugby', value: 'rugby' }
  ];

  stats = signal({
    pending: 0,
    processing: 0,
    completed: 0,
    totalRevenue: 0
  });

  ngOnInit() {
    this.loadCustomRequests();
  }

  loadCustomRequests() {
    this.isLoading.set(true);

    this.predictionService.getPurchaseRequests().subscribe({
      next: (requests) => {
        const reversed = [...requests].reverse();
        this.customRequests.set(reversed);
        this.calculateStats(reversed);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Erreur lors du chargement des demandes personnalisées:', error);
        this.isLoading.set(false);
      }
    });

  }

  calculateStats(requests: CustomPredictionRequest[]) {
    const stats = {
      pending: requests.filter(r => r.status === 'pending').length,
      processing: requests.filter(r => r.status === 'processing').length,
      completed: requests.filter(r => r.status === 'completed').length,
      totalRevenue: requests.filter(r => r.status === 'completed').length * 5
    };
    this.stats.set(stats);
  }

  getFilteredRequests() {
    let filtered = this.customRequests();

    if (this.statusFilter) {
      filtered = filtered.filter(r => r.status === this.statusFilter);
    }

    if (this.sportFilter) {
      filtered = filtered.filter(r => r.requested_sport_name.toLowerCase() === this.sportFilter.toLowerCase());
    }

    if (this.dateFilter) {
      const filterDate = this.dateFilter.toDateString();
      filtered = filtered.filter(r =>
        new Date(r.requested_event_date).toDateString() === filterDate
      );
    }
    console.log('Filtered requests:', filtered);
    // sort by purchase_date desc
    filtered = filtered.sort((a, b) => new Date(a.requested_event_date).getTime() - new Date(b.requested_event_date).getTime());

    return filtered;
  }

  viewRequestDetails(request: CustomPredictionRequest) {
    this.selectedRequest = request;
    this.showDetailsDialog = true;
  }

  startProcessing(request: CustomPredictionRequest) {
    this.selectedRequest = request;
    this.showPredictionFormDialog = true;
  }

  markAsProcessing(request: CustomPredictionRequest) {
    this.confirmationService.confirm({
      message: `Marquer la demande de ${request.user_name} comme "En cours" ?`,
      header: 'Confirmer le changement de statut',
      icon: 'pi pi-question-circle',
      acceptLabel: 'Confirmer',
      rejectLabel: 'Annuler',
      accept: () => {
        this.predictionService.markRequestAsProcessed(request.id).subscribe({
          next: () => {
            this.updateRequestStatus(request.id, 'processing');
            this.messageService.add({
              severity: 'success',
              summary: 'Statut mis à jour',
              detail: `La demande de ${request.user_name} a été marquée comme "En cours"`,
              life: 3000
            });
          },
          error: (error) => {
            console.error('Erreur lors de la mise à jour du statut:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Erreur',
              detail: 'Impossible de mettre à jour le statut de la demande',
              life: 5000
            });
          }
        });

      }
    });
  }

  cancelRequest(request: CustomPredictionRequest) {
    this.selectedRequest = request;
    this.cancelNote = '';
    this.cancelDialogVisible = true;
  }

  updateRequestStatus(requestId: number | null, newStatus: string) {
    const requests = this.customRequests();
    const updatedRequests = requests.map(r =>
      r.id === requestId ? { ...r, status: newStatus as any } : r
    );
    this.customRequests.set(updatedRequests);
    this.calculateStats(updatedRequests);

    this.messageService.add({
      severity: 'success',
      summary: 'Statut mis à jour',
      detail: `La demande a été marquée comme "${newStatus}"`,
      life: 3000
    });
  }

  onPredictionCreated(predictionData: any) {

    this.customErrors = {
      dateError: new Date(predictionData.eventDate) < new Date(),
      isEqualTeams: predictionData.teamHomeName === predictionData.teamAwayName
    };
    if ( this.customErrors.dateError || this.customErrors.isEqualTeams) return;
    if (!this.selectedRequest) return;


    this.predictionService.fulfillPurchaseRequest(this.selectedRequest.id, {
      title: predictionData.title,
      sport: parseInt(predictionData.sport),
      competition: parseInt(predictionData.competition),
      team_home: parseInt(predictionData.teamHomeName),
      team_away: parseInt(predictionData.teamAwayName),
      confidence_team_home: predictionData.teamHomeConfidence ? parseInt(predictionData.teamHomeConfidence) / 100 : 0,
      confidence_team_away: predictionData.teamAwayConfidence ? parseInt(predictionData.teamAwayConfidence) / 100 : 0,
      event_name: predictionData.eventName,
      event_date: predictionData.eventDate,
      analysis_text: predictionData.analysisText,
      created_by: parseInt(JSON.parse(localStorage.getItem('user') || '{}').user_id),
      price_jetons: 15,
    }).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Prédiction créée',
          detail: `La prédiction pour ${predictionData.eventName} a été créée avec succès`,
          life: 3000
        });
        if (this.selectedRequest) this.updateRequestStatus(this.selectedRequest.id, 'completed');
      },
      error: (error) => {
        console.error('Erreur lors de la création de la prédiction:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: 'Impossible de créer la prédiction',
          life: 5000
        });
      }
    });



    this.updateRequestStatus(this.selectedRequest!.id, 'completed');
    this.showPredictionFormDialog = false;
    this.selectedRequest = null;

  }

  onPredictionCancelled() {
    this.showPredictionFormDialog = false;
    this.selectedRequest = null;
  }

  getStatusSeverity(status: string): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    switch (status) {
      case 'pending': return 'warn';
      case 'processing': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'danger';
      default: return 'secondary';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'pending': return 'En attente';
      case 'processing': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  }

  getSportClass(sport: string): string {
    switch (sport.toLowerCase()) {
      case 'football': return 'bg-green-100 text-green-800';
      case 'basketball': return 'bg-orange-100 text-orange-800';
      case 'tennis': return 'bg-yellow-100 text-yellow-800';
      case 'rugby': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }

  applyFilters() {
    this.messageService.add({
      severity: 'info',
      summary: 'Filtres appliqués',
      detail: `${this.getFilteredRequests().length} résultat(s) trouvé(s)`,
      life: 3000
    });
  }

  clearFilters() {
    this.statusFilter = '';
    this.sportFilter = '';
    this.dateFilter = null;
  }



  refreshData() {
    this.loadCustomRequests();
    this.messageService.add({
      severity: 'success',
      summary: 'Données actualisées',
      detail: 'La liste a été mise à jour',
      life: 3000
    });
  }
  confirmCancel() {
    if (this.selectedRequest) {
      this.predictionService.markRequestAsCancelled(this.selectedRequest.id, this.cancelNote).subscribe({
        next: () => {
          this.updateRequestStatus(this.selectedRequest!.id, 'cancelled');
          this.messageService.add({
            severity: 'success',
            summary: 'Demande annulée',
            detail: `La demande de ${this.selectedRequest!.user_name} a été annulée`,
            life: 3000
          });
          this.cancelDialogVisible = false;
          this.selectedRequest = null;
        },
        error: (error) => {
          console.error('Erreur lors de l\'annulation de la demande:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Impossible d\'annuler la demande',
            life: 5000
          });
        }
      });
      this.cancelDialogVisible = false;
    }
  }
}